import { getRolesUserAndSwitchUser } from '@/lib/auth/getRolesUserAndSwitchUser'
import {
  allRoutesAdmin,
  allRoutesApplication,
  allRoutesFront,
  allRoutesFrontGroupementChain,
} from '@/lib/routes/CONSTANT_ROUTES'

/**
 * Route « utilitaire » (`utility: true`) : page transverse ouverte à tous les rôles, qu'on
 * n'atteint que par un lien précis (ex. validation d'une connexion par QR code). Elle reste
 * accessible par URL et résoluble par son nom, mais n'est NI une page d'accueil, NI la
 * preuve qu'un utilisateur a accès à son entité.
 *
 * Sans ce marqueur, une telle route devient la seule page Group d'un compte qui n'en a
 * aucune autre : un compte comptoir (Care) se voyait attribuer l'entité Group et atterrissait
 * sur la page de validation QR, vide, au lieu de son tableau de bord Care.
 */
export const isUtilityRoute = (route: { route?: string; utility?: boolean }): boolean =>
  route?.utility === true

const getListRouteAvailable = (loggedUserRoles, entity): any[] => {
  return allRoutesApplication.filter(
    route =>
      !isUtilityRoute(route) &&
      route.entity.includes(entity) &&
      (!route.roles || !!route.roles.find(roles => loggedUserRoles?.includes(roles)))
  )
}

export const getListRouteAvailableWithoutEntity = loggedUserRoles => {
  return allRoutesApplication.filter(
    route => !route.roles || !!route.roles.find(roles => loggedUserRoles?.includes(roles))
  )
}

// getListRoutesHome DÉPLACÉE dans lib/routes/getListRoutesHome : elle consommait getRoutes,
// qui importe ce fichier — c'était le cycle routes ↔ getRoutes, traversé par toutes les pages.

// Construit le path (with values + queryParams substitution) a partir d'une entree de route.
const buildPathFromRoute = (routeFind: any, values: Record<string, any> = {}): string => {
  const routeSelect = routeFind?.href

  let as = routeSelect ? `${routeSelect}` : '/'
  Object.keys(values).forEach(key => {
    const val = encodeURIComponent(String(values[key]))
    // Replace bracket placeholders like [id] or [key]
    as = as.replace(new RegExp(`\\[${key}\\]`, 'g'), val)
    // Backward-compat: also replace plain key tokens if present as standalone segments
    as = as.replace(new RegExp(`(^|/):?${key}($|/|\\?)`, 'g'), (_m, p1, p2) => `${p1}${val}${p2}`)
  })

  if (routeFind?.queryParams) {
    Object.keys(routeFind.queryParams).forEach(key => {
      const qpVal = encodeURIComponent(String(routeFind.queryParams[key]))
      as = as.replace(new RegExp(`\\[${key}\\]`, 'g'), qpVal)
      as = as.replace(
        new RegExp(`(^|/):?${key}($|/|\\?)`, 'g'),
        (_m, p1, p2) => `${p1}${qpVal}${p2}`
      )
    })
  }

  return as
}

// Sync helpers: build path and absolute URL without async dependencies
export const getRouteFrontPathByNameSync = (
  routeName: string,
  values: Record<string, any> = {}
) => {
  const routeFind = allRoutesFront.find(r => r.route === routeName)
  return buildPathFromRoute(routeFind, values)
}

// Resout un nom de route contre les routes REELLEMENT disponibles pour l'utilisateur courant
// (filtrees par ses roles), sans dependance async (pas de resolution d'entite).
// Priorite aux routes FRONT (preserve le comportement pharmacien existant), puis ADMIN
// (corrige les users industry dont la route 'MyProfile' vit dans allRoutesAdmin =
// /backoffice/common/my-profile), en passant par l'espace GROUPEMENT / CHAINE : sans lui, un
// manager de groupement retombait sur le profil pharmacien (/frontoffice/group/my-profile),
// que withAuthent lui refuse. Fallback : route front brute (comportement historique).
export const getRoutePathByNameForUserSync = (
  routeName: string,
  values: Record<string, any> = {}
): string => {
  const { loggedUserRoles } = getRolesUserAndSwitchUser()
  const matchByRole = (route: any) =>
    route.route === routeName &&
    (!route.roles || !!route.roles.find((role: string) => loggedUserRoles?.includes(role)))

  const frontMatch = allRoutesFront.find(matchByRole)
  if (frontMatch?.href) return buildPathFromRoute(frontMatch, values)

  const groupementChainMatch = allRoutesFrontGroupementChain.find(matchByRole)
  if (groupementChainMatch?.href) return buildPathFromRoute(groupementChainMatch, values)

  const adminMatch = allRoutesAdmin.find(matchByRole)
  if (adminMatch?.href) return buildPathFromRoute(adminMatch, values)

  return getRouteFrontPathByNameSync(routeName, values)
}

/**
 * Chemin d'une route backoffice, sans await (venu de develop, reimplemente sur
 * buildPathFromRoute) : pour les chemins calcules dans un rendu (lien de cellule de
 * tableau). Ne filtre pas par role — a reserver aux ecrans deja restreints.
 */
export const getRouteAdminPathByNameSync = (
  routeName: string,
  values: Record<string, any> = {}
) => {
  const routeFind = allRoutesAdmin.find(r => r.route === routeName)
  return buildPathFromRoute(routeFind, values)
}

export const getAbsoluteUrlByRouteFrontNameSync = (
  routeName: string,
  values: Record<string, any> = {},
  origin?: string
) => {
  const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : '')
  const path = getRouteFrontPathByNameSync(routeName, values)
  return `${base}${path}`
}

export default getListRouteAvailable
